import { useEffect, useState } from "react";
import api, { fmtIDR } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fl } from "@/lib/finI18n";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Package } from "lucide-react";
import { useListKeys } from "@/lib/useListKeys";

export const blankItem = () => ({ product_id: "", name: "", category: "goods", unit: "", qty: "", unit_price: "", total: "" });
const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const catOf = (p) => (p.category === "material" ? "material" : p.category === "finished" ? "finished" : "goods");

function ProductName({ products, value, onChange, onPick, testid }) {
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const typed = (value || "").trim().toLowerCase();
  const list = products.filter((p) => !typed || p.name.toLowerCase().includes(typed)).slice(0, 12);
  const exact = products.some((p) => p.name.toLowerCase() === typed);
  const keys = useListKeys(list, { open, setOpen, onPick, shown: open && (list.length > 0 || !!typed) });
  return (
    <div className="relative">
      <Input value={value} autoComplete="off" className="h-8" placeholder={fl("itemName", lang)} data-testid={testid}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }} {...keys.input}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} />
      {open && (list.length > 0 || typed) && (
        <div ref={keys.box} className="absolute z-50 mt-1 w-72 rounded-md border border-border bg-popover shadow-lg max-h-56 overflow-auto" data-testid={`${testid}-suggestions`}>
          {list.map((p, i) => (
            <button type="button" key={p.id} {...keys.opt(i)} onMouseDown={(e) => e.preventDefault()} onClick={() => { onPick(p); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-accent data-[active]:bg-accent transition-colors" data-testid={`${testid}-opt-${p.id}`}>
              <span className="truncate">{p.name}</span>
              <span className="text-[11px] text-muted-foreground shrink-0">{fl(`cat_${catOf(p)}`, lang)} · {Number(p.stock || 0).toLocaleString("id-ID")} {p.unit}</span>
            </button>
          ))}
          {typed && !exact && <div className="px-3 py-2 text-[11px] text-muted-foreground border-t border-border">{fl("newProduct", lang)}</div>}
        </div>
      )}
    </div>
  );
}

export function PurchaseItems({ clientId, items, onChange, currency }) {
  const { lang } = useI18n();
  const [products, setProducts] = useState([]);
  useEffect(() => {
    if (clientId) api.get(`/invoice-products?client_id=${clientId}`).then(({ data }) => setProducts(data.filter((p) => p.track_stock || p.item_type === "barang"))).catch(() => {});
  }, [clientId]);

  const upd = (i, patch) => onChange(items.map((it, idx) => {
    if (idx !== i) return it;
    const n = { ...it, ...patch };
    if ("qty" in patch || "unit_price" in patch) n.total = r2(Number(n.qty || 0) * Number(n.unit_price || 0));
    if ("total" in patch && Number(n.qty)) n.unit_price = Math.round((Number(n.total || 0) / Number(n.qty)) * 10000) / 10000;
    return n;
  }));
  const total = r2(items.reduce((s, it) => s + (Number(it.total) || 0), 0));
  const money = (v) => (currency && currency !== "IDR" ? `${currency} ${Number(v).toLocaleString("id-ID", { maximumFractionDigits: 2 })}` : fmtIDR(v));

  return (
    <div className="sm:col-span-2 min-w-0 rounded-md border border-border" data-testid="purchase-items">
      <div className="flex items-center justify-between px-3 py-2 bg-secondary/40 border-b border-border">
        <span className="text-sm font-medium flex items-center gap-1.5"><Package className="h-4 w-4" />{fl("purchItems", lang)}</span>
        <Button type="button" size="sm" variant="outline" onClick={() => onChange([...items, blankItem()])} data-testid="purchase-add-item"><Plus className="h-3.5 w-3.5 mr-1" />{fl("addItem", lang)}</Button>
      </div>
      <div className="hidden md:grid grid-cols-12 gap-2 px-3 pt-2 text-[11px] text-muted-foreground">
        <span className="col-span-3">{fl("itemName", lang)}</span><span className="col-span-2">{fl("itemCat", lang)}</span><span>{fl("unit", lang)}</span>
        <span>{fl("qty", lang)}</span><span className="col-span-2 text-right">{fl("unitPrice", lang)}</span><span className="col-span-2 text-right">{fl("lineTotal", lang)}</span><span />
      </div>
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-2 md:grid-cols-12 gap-2 px-3 py-2 border-b border-border/60 last:border-0" data-testid={`purchase-item-${i}`}>
          <div className="col-span-2 md:col-span-3">
            <ProductName products={products} value={it.name} testid={`purchase-item-name-${i}`}
              onChange={(v) => upd(i, { name: v, product_id: "" })}
              onPick={(p) => upd(i, { product_id: p.id, name: p.name, unit: p.unit || it.unit, category: catOf(p) })} />
          </div>
          <div className="md:col-span-2">
            <Select value={it.category} onValueChange={(v) => upd(i, { category: v })} disabled={!!it.product_id}>
              <SelectTrigger className="h-8 text-xs" data-testid={`purchase-item-cat-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="goods">{fl("cat_goods", lang)}</SelectItem>
                <SelectItem value="material">{fl("cat_material", lang)}</SelectItem>
                {it.category === "finished" && <SelectItem value="finished">{fl("cat_finished", lang)}</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <Input className="h-8 md:col-span-1" placeholder={fl("unit", lang)} value={it.unit} onChange={(e) => upd(i, { unit: e.target.value })} data-testid={`purchase-item-unit-${i}`} />
          <Input className="h-8 num md:col-span-1" type="number" min="0" placeholder={fl("qty", lang)} value={it.qty} onChange={(e) => upd(i, { qty: e.target.value })} data-testid={`purchase-item-qty-${i}`} />
          <Input className="h-8 num text-right md:col-span-2" type="number" min="0" placeholder={fl("unitPrice", lang)} value={it.unit_price} onChange={(e) => upd(i, { unit_price: e.target.value })} data-testid={`purchase-item-price-${i}`} />
          <Input className="h-8 num text-right md:col-span-2" type="number" min="0" placeholder={fl("lineTotal", lang)} value={it.total} onChange={(e) => upd(i, { total: e.target.value })} data-testid={`purchase-item-total-${i}`} />
          <div className="flex items-center justify-end md:col-span-1">
            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={items.length === 1} onClick={() => onChange(items.filter((_, idx) => idx !== i))} data-testid={`purchase-item-del-${i}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        </div>
      ))}
      <div className="flex items-center justify-between px-3 py-2 text-sm font-semibold bg-secondary/30">
        <span>{fl("total", lang)} DPP</span><span className="num" data-testid="purchase-items-total">{money(total)}</span>
      </div>
      <p className="px-3 pb-2 text-[11px] text-muted-foreground">{fl("purchAutoCoa", lang)}</p>
    </div>
  );
}
